import type { QueryClient } from '@tanstack/react-query';
import type { AuthenticatedUser } from '@24talk/shared';
import { api, ApiError } from './api';

export interface SessionData {
  user: AuthenticatedUser | null;
}

export async function loadSession(signal?: AbortSignal): Promise<SessionData> {
  try {
    return await api<SessionData>('/api/v1/auth/me', signal ? { signal } : undefined);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return { user: null };
    throw error;
  }
}

export async function clearSession(client: QueryClient): Promise<void> {
  // Cancel first so a late /me or workspace response cannot restore an old user's data.
  await client.cancelQueries();
  client.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' });
  client.getMutationCache().clear();
  client.setQueryData<SessionData>(['me'], { user: null });
}

export async function logoutSession(client: QueryClient): Promise<void> {
  try {
    await api('/api/v1/auth/logout', {
      method: 'POST',
      signal: AbortSignal.timeout(15_000),
    });
  } catch (error) {
    // Older API deployments may still reject logout for an absent/expired session.
    if (!(error instanceof ApiError && error.status === 401)) throw error;
  }
  await clearSession(client);
}
